<script setup lang="ts">
import { computed } from "vue";

import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import { useFeatureFlag } from "@/composables/feature-flag/use-feature-flag";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { TMultisiteMissingInfos } from "@/data/api/kili/consumption/types/multisite-consumption.type";
import { useTypedIntl } from "@/locales/typed-intl";
import { ERouteName } from "@/router/router.type";
import { newSectionManagementFeatureFlag } from "@/services/feature-flags/env-feature-flags";

import DashboardDialog from "@/components/data/dashboard/dashboard-dialog.vue";
import ColoredBadge from "@/components/ui/colored-badge.vue";

interface IProps {
  missingInfos?: TMultisiteMissingInfos;
  noData: boolean | undefined;
}

const visible = defineModel<boolean>();
const props = defineProps<IProps>();

const { t, k, rawT } = useTypedIntl();
const { isEnvFeatureEnabled } = useFeatureFlag();

const subtitle = computed<string>(() => {
  return rawT(
    `data.dashboard.missingSurfaceArea.subtitle.${props.noData ? "total" : "partial"}.${EDashboardType.MULTISITE}`,
  );
});
</script>

<template>
  <DashboardDialog v-model:visible="visible">
    <template #title>
      <h4>
        {{ t(k.data.dashboard.missingSurfaceArea.title) }}
      </h4>
    </template>

    <template #subtitle>
      <span class="text-text-secondary">
        {{ subtitle }}
      </span>
    </template>

    <ul class="flex flex-col gap-2">
      <li
        v-for="site in props.missingInfos?.missingAreaSites"
        :key="site.id"
      >
        <router-link
          :data-site-id="site.id"
          :data-test-id="DATA_TEST_IDS.views.dashboard.missingInfos.multi.missingSurfaceArea.siteLink"
          target="_blank"
          :to="{
            name: isEnvFeatureEnabled(newSectionManagementFeatureFlag)
              ? ERouteName.SITE_SECTIONS_TREE
              : ERouteName.SITE_SECTIONS,
            params: { siteId: site.id },
          }"
        >
          <div class="p-4 rounded-xl border flex items-center gap-2">
            <ColoredBadge
              :data-test-id="DATA_TEST_IDS.views.dashboard.missingInfos.multi.missingSurfaceArea.siteBadge"
              :name="site.name"
            />
            <h5
              class="flex-1"
              :data-test-id="DATA_TEST_IDS.views.dashboard.missingInfos.multi.missingSurfaceArea.siteName"
            >
              {{ site.name }}
            </h5>
            <span class="font-semibold text-text-secondary">
              {{ t(k.data.dashboard.missingSurfaceArea.title) }}
            </span>

            <i class="pi pi-arrow-right !text-xs" />
          </div>
        </router-link>
      </li>
    </ul>
  </DashboardDialog>
</template>
